import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    AnimatedIconPartsDynamicStyleType,
    AnimatedIconPartConstantType,
    AnimatedIconPartAttributes
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    AnimatedIconPartProperty,
    AnimatedIconPart
} from "../../type";
import {
    ANIMATED_ICON_STROKE_MIN_SCALE,
    ANIMATED_ICON_ANGLE_PRECISION,
    ANIMATED_ICON_SCALE_EPSILON,
    ANIMATED_ICON_DASH_PADDING,
    ANIMATED_ICON_DASH_SCALE
} from "../../constant";
import {
    getMirrorStyle
} from "../../../../helpers/direction";
import {
    getAnimatedIconRangeValue
} from "../../util";

const PART_REST_VALUES: Record<AnimatedIconPartProperty, number> = {
    translateX: 0,
    translateY: 0,
    opacity: 1,
    rotate: 0,
    scaleX: 1,
    scaleY: 1,
    dash: 0,
    blur: 0
};

const PART_TRANSFORM_PROPERTIES: Array<AnimatedIconPartProperty> = [
    "translateX",
    "translateY",
    "rotate",
    "scaleX",
    "scaleY"
];

const getFixed = (value: number): number => {
    return Number(value.toFixed(ANIMATED_ICON_ANGLE_PRECISION));
};

export const getAnimatedIconPartValue = ({
    progress,
    property,
    part
}: {
    property: AnimatedIconPartProperty;
    part: AnimatedIconPart;
    progress: number;
}): number => {
    const range = part.ranges[property];

    if(!range) {
        return PART_REST_VALUES[property];
    }

    return getAnimatedIconRangeValue({
        progress,
        range
    });
};

export const getAnimatedIconPartAttributes = ({
    strokeWidth,
    progress,
    part,
    node
}: AnimatedIconPartConstantType): AnimatedIconPartAttributes => {
    const attributes: AnimatedIconPartAttributes = {};

    const getValue = (property: AnimatedIconPartProperty) => getAnimatedIconPartValue({
        progress,
        property,
        part
    });

    if(part.ranges.opacity) {
        attributes.opacity = getFixed(getValue("opacity"));
    }

    if(part.isDashed) {
        const length = node.length * ANIMATED_ICON_DASH_SCALE + ANIMATED_ICON_DASH_PADDING;

        attributes.strokeDasharray = `${getFixed(length)} ${getFixed(length)}`;

        attributes.strokeDashoffset = getFixed(length * getValue("dash"));
    }

    if(PART_TRANSFORM_PROPERTIES.some((property) => part.ranges[property])) {
        const [
            originX,
            originY
        ] = part.origin;

        const rotation = getValue("rotate") * Math.PI / 180;

        const scaleX = getValue("scaleX");

        const scaleY = getValue("scaleY");

        const a = Math.cos(rotation) * scaleX;

        const b = Math.sin(rotation) * scaleX;

        const c = -Math.sin(rotation) * scaleY;

        const d = Math.cos(rotation) * scaleY;

        const e = getValue("translateX") + originX - (a * originX + c * originY);

        const f = getValue("translateY") + originY - (b * originX + d * originY);

        if(part.isStrokeConstant) {
            attributes.strokeWidth = Math.abs(scaleX - scaleY) < ANIMATED_ICON_SCALE_EPSILON ? getFixed(strokeWidth / Math.max(Math.abs(scaleX), ANIMATED_ICON_STROKE_MIN_SCALE)) : strokeWidth;
        }

        attributes.transform = `matrix(${[
            a,
            b,
            c,
            d,
            e,
            f
        ].map(getFixed).join(" ")})`;
    }

    return attributes;
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center"
    }
});

export const useStyles = ({
    isMirrored,
    direction,
    size
}: AnimatedIconPartsDynamicStyleType) => {
    const styles = {
        container: {
            ...getMirrorStyle({
                isMirrored,
                direction
            }),
            height: size,
            width: size
        } as Mutable<ViewStyle>
    };

    return styles;
};
export default stylesheet;
